import CrytoJs from 'crypto-js';
import moment from 'moment';
import currencyCodes from 'currency-codes';
import { getCode, getData, overwrite, getNames, getName } from 'country-list';
import queryString from 'query-string';
import * as ibantools from 'ibantools';
import toCamelCase from 'to-camel-case';
import react, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import * as ServerConfigs from '../configs/server.configs';
import currencyHelper from '../helpers/currencyHelper';

const setCookie = (name, value, minutes = 30) => {
  const date = new Date(Date.now() + minutes * 60 * 1000);
  const expires = `expires= ${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires}`;
};

const deleteCookie = (name) => {
  setCookie(name, '', -1);
};

const getCookie = (name) => {
  // eslint-disable-next-line no-param-reassign
  name = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

const setLocalStorage = (name, value) => {
  window.localStorage.setItem(name, value);
};

const getLocalStorage = (name) => {
  return window.localStorage.getItem(name);
};

const deleteLocalStorage = (name) => {
  window.localStorage.removeItem(name);
};

const loadNewBody = (newClass) => {
  if (newClass.length > 0 && Array.isArray(newClass)) {
    document.body.classList.remove('2-columns');
    newClass.forEach((className) => {
      document.body.classList.add(className);
    });
  }
};

const revertBody = (prevClass) => {
  if (prevClass.length > 0 && Array.isArray(prevClass)) {
    document.body.classList.add('2-columns');
    prevClass.forEach((className) => {
      document.body.classList.remove(className);
    });
  }
};

const checkCompanyExist = (companyList, company) => {
  let isExist = false;
  if (companyList.length) {
    isExist = companyList.some((x) => {
      return x.Alias === company;
    });
  }
  return isExist;
};

const getCurrentCompany = (props) => {
  // required withRouter;
  const { match } = props;
  return match.params.company;
};

export const getSelectCompany = (companyList, companyId) => {
  // required withRouter;
  const company = companyList.find((x) => companyId === x.CompanyId);
  return company;
};

const renderHTML = (rawHTML, tag = 'div') => react.createElement(tag, { dangerouslySetInnerHTML: { __html: rawHTML } });

const isHtml = (str) => {
  var doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

const formatCommon = {
  datePlus: 'YYYY-MM-DD',
  dateDot: 'DD.MM.YYYY',
  dateText: 'MMMM Do, YYYY',
};

const formatDatabase = (time) => moment(time).format(formatCommon.datePlus);

const formatView = (time) => moment(time).format(formatCommon.dateText);

const bankNumber = (value) => {
  const onlyNums = value ? value.replace(/[^\d]/g, '') : '';
  if (onlyNums.length <= 4) {
    return onlyNums;
  }
  if (onlyNums.length <= 6) {
    return `${onlyNums.slice(0, 4)}.${onlyNums.slice(4)}`;
  }
  return `${onlyNums.slice(0, 4)}.${onlyNums.slice(4, 6)}.${onlyNums.slice(6, 11)}`;
};

const dateFormat = (date) => {
  return date.replace(/^([\d]{2})([\d]{2})([\d]{3})$/, '$1/$2/$3');
};

const capitalize = (str) => {
  // capitalize first letter
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

const capitalizeSpace = (str) => {
  // MySite = My Site
  return str.replace(/([A-Z])/g, ' $1').trim();
};

// get query string to object
const getJsonFromUrl = (url) => {
  if (!url) url = window.location.search;
  const query = url.substr(1);
  const result = {};
  query.split('&').forEach((part) => {
    const item = part.split('=');
    result[item[0]] = decodeURIComponent(item[1]);
  });
  return result;
};

export const formatString = (string) => {
  let str = string.toString().trim().toLowerCase();
  const from = 'åãàáäâẽèéëêìíïîõòóöôùúüûñçø·/_,:;';
  const to = 'aaaaaaeeeeeiiiiooooouuuunco------';
  for (let i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
  }
  return str;
};

export const getTimeZone = () => {
  const {
    location: { hostname },
  } = window;
  const localHosts = ['localhost', 'dev.bilagsky.no', 'www.dev.bilagsky.no'];
  const local = localHosts.includes(hostname);
  const timezone = local ? 'Asia/Singapore' : 'Europe/Oslo';
  return timezone;
};

export const slugify = (string) => {
  let str = string.toString().trim().toLowerCase();
  const from = 'åãàáäâẽèéëêìíïîõòóöôùúüûñçø·/_,:;';
  const to = 'aaaaaaeeeeeiiiiooooouuuunco------';
  for (let i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
  }
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-'); // collapse dashes
  return str;
};

export const getUserLogin = () => {
  if (localStorage.getItem('_user')) {
    const _user = JSON.parse(localStorage.getItem('_user'));
    const userRoleToken = ServerConfigs[process.env.NODE_ENV].tokenSecret;
    const bytes = CrytoJs.AES.decrypt(_user.RoleId, userRoleToken);
    if (bytes.sigBytes === 0) return null;
    const decode = JSON.parse(bytes.toString(CrytoJs.enc.Utf8));
    _user.RoleId = decode;
    return _user;
  }
  return null;
};

export const getUserRole = (roleId) => {
  switch (roleId) {
    case 2:
      return 'Admin';
    case 3:
      return 'Accountant';
    case 4:
      return 'Accountant Limit';
    case 5:
      return 'VN Accountant';
    case 6:
      return 'Recruitment';
    case 7:
      return 'Viet Nam Admin';
    case 14:
      return 'CEO';
    case 15:
      return 'HR Eco Finans AS';
    case 16:
      return 'HR Oryton';
    case 17:
      return 'Developer';
    case 18:
      return 'Admin Recruitment';
    default:
      return null;
  }
};

export const rebuildRouterUrl = (router) => {
  // const { match: { params: { company } } } = this.props;
  const {
    location: { pathname },
  } = window;
  if (pathname) {
    const alias = pathname.split('/')[1];
    if (alias) return router.replace(':company', alias);
  }
  return router;
};

// If str contains a corp-suffix word (AS|ASA|DA|ANS|AB), capitalize the first letter of each word before it.
// Otherwise, just capitalize the first letter of the whole string.
export const capitalizeLetter = (str) => {
  if (!str) return '';

  const CORP_SUFFIX = /^(AS|ASA|DA|ANS|AB)/i;

  const capitalizeFirst = (word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word);
  const titleCase = (word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word);

  const words = str.split(' ');
  const suffixIndex = words.findIndex((word) => CORP_SUFFIX.test(word));

  if (suffixIndex === -1) {
    return capitalizeFirst(str);
  }

  return words.map((word, i) => (i < suffixIndex ? titleCase(word) : word)).join(' ');
};

export const getParameterByName = (name, url = decodeURIComponent(window.location.href)) => {
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const formatKidMessage = (value) => {
  if (value) {
    return value.length < 140 ? value : value.substr(0, 140);
  }
  return '';
};

export const qsstringify = (data) => {
  return queryString.stringify(data);
};

export const qsparse = (data) => {
  return queryString.parse(data);
};

export const getAccessToken = () => {
  if (localStorage.getItem('_user')) {
    const _user = JSON.parse(localStorage.getItem('_user'));
    const { AccessToken } = _user;
    return AccessToken;
  }
  return null;
};

// temp: only allow show payment action on staging and dev not production
export const allowPaymentAction = () => {
  // const { host } = window.location;
  return true;
  // return host !== 'bilagsky.no';
};

export const kontrollsiffferMod11 = (value) => {
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const kontonummerWithoutSpacesAndPeriods = value.replace(/[\s.]+/g, '');
  if (kontonummerWithoutSpacesAndPeriods.length !== 11) {
    return false;
  } else {
    if (kontonummerWithoutSpacesAndPeriods === '00000000000') return false;

    const sjekksiffer = parseInt(kontonummerWithoutSpacesAndPeriods.charAt(10), 10);
    const kontonummerUtenSjekksiffer = kontonummerWithoutSpacesAndPeriods.substring(0, 10);
    let sum = 0;
    for (let index = 0; index < 10; index++) {
      sum += parseInt(kontonummerUtenSjekksiffer.charAt(index), 10) * weights[index];
    }
    const remainder = sum % 11;
    return sjekksiffer === (remainder === 0 ? 0 : 11 - remainder);
  }
};

const regexDate = '^([0]?[1-9]|[1|2][0-9]|[3][0|1])[./-]([0]?[1-9]|[1][0-2])[./-]([0-9]{4}|[0-9]{2})$';
const regexNaUy = '^([0-9]{4}|[0-9]{2})[./-]([0]?[1-9]|[1][0-2])[./-]([0]?[1-9]|[1|2][0-9]|[3][0|1])$';

const IMPORT_DATE_FORMATS = ['DD.MM.YYYY', 'DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY.MM.DD', 'YYYY-MM-DD', 'YYYY/MM/DD'];

const normalizeImportDateString = (raw) => {
  if (typeof raw !== 'string') {
    return raw;
  }
  const parsed = moment(raw.trim(), IMPORT_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : raw;
};

const EXCEL_DATE_CELL_FORMATS = [
  ...IMPORT_DATE_FORMATS,
  ...IMPORT_DATE_FORMATS.map((f) => `${f} HH:mm:ss.SSS`),
  ...IMPORT_DATE_FORMATS.map((f) => `${f} HH:mm:ss`),
  ...IMPORT_DATE_FORMATS.map((f) => `${f} HH:mm`),
];

const DAY_MS = 24 * 60 * 60 * 1000;

// Date cell ("t": "d") read by the server with SheetJS `cellDates: true`.
// `w` is the text Excel displays (timezone independent) -> use it when it is a known date format.
// `v` is a JS Date created at local midnight of the SERVER timezone (with a few minutes drift from SheetJS),
// then serialized to UTC, e.g. 01.09.2026 on an Oslo server -> "2026-08-31T21:43:00.000Z".
// Formatting it in any timezone gives the wrong day, so round it to the nearest UTC day instead.
const formatExcelDateCell = ({ w, v }) => {
  if (typeof w === 'string') {
    const parsed = moment(w.trim(), EXCEL_DATE_CELL_FORMATS, true);
    if (parsed.isValid()) return parsed.format('DD.MM.YYYY');
  }
  const time = new Date(v).getTime();
  if (Number.isNaN(time)) return normalizeImportDateString(w ?? '');
  return moment.utc(Math.round(time / DAY_MS) * DAY_MS).format('DD.MM.YYYY');
};

const formatExcelCell = (cell) => {
  if (!cell) return '';
  if (cell.t === 'd') return formatExcelDateCell(cell);
  return normalizeImportDateString(cell.w ?? cell.v ?? '');
};

const isDateString = (value) => typeof value === 'string' && !!(value.match(regexDate) || value.match(regexNaUy));

// Column letters -> number, e.g. A -> 1, Z -> 26, AA -> 27
const columnLettersToNumber = (letters) => [...letters].reduce((total, char) => total * 26 + char.charCodeAt(0) - 64, 0);

const isAmountValue = (rawValue) => {
  if (rawValue === undefined || rawValue === null) return false;
  const value = `${rawValue}`;
  const hasSeparator = value.includes(',') || value.includes('.');
  if (!hasSeparator && !Number.isFinite(+value)) return false;

  const formatted = currencyHelper.formatMoney(value);
  const isFormattedNumber =
    formatted !== value.replace(/ /g, '') || (formatted === value && !isNaN(Number(formatted.replace(/,/g, '.'))));
  if (!isFormattedNumber) return false;

  return hasSeparator || +formatted?.replace(/ /g, '').replace(/,/g, '.') < 0;
};

const mapDataExelToTable = (data) => {
  try {
    const columnSet = new Set();
    const rowSet = new Set();
    Object.keys(data).forEach((key) => {
      // skip sheet meta keys (!ref, !margins, !rows, !merges, ...)
      const match = key.match(/^([A-Z]+)([0-9]+)$/);
      if (!match) return;
      columnSet.add(match[1]);
      rowSet.add(match[2]);
    });

    const columns = [...columnSet].sort((a, b) => columnLettersToNumber(a) - columnLettersToNumber(b));
    const rows = [...rowSet].sort((a, b) => a - b);

    const result = rows.map((row) => {
      const dataInRow = columns.map((column) => formatExcelCell(data[`${column}${row}`]));
      return { data: dataInRow, isHeader: !dataInRow.some(isDateString) };
    });

    const columnDetects = columns.map((val, index) => ({
      val,
      isAmount: result.some((r) => isAmountValue(r.data[index])),
    }));

    return { result, columns, columnDetects };
  } catch (error) {
    return { result: [], columns: [], columnDetects: [] };
  }
};

const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      resolve(reader.result);
    };
  });
};

const eventResizer = (id, reset) => {
  // Query the element
  const resizer = document.getElementById(id);
  const leftSide = resizer.previousElementSibling;
  const rightSide = resizer.nextElementSibling;

  // The current position of mouse
  let x = 0;
  let y = 0;
  let leftWidth = 0;
  let rightWidth = 0;

  // Handle the mousedown event
  // that's triggered when user drags the resizer
  const mouseDownHandler = function (e) {
    // Get the current mouse position
    x = e.clientX;
    y = e.clientY;
    leftWidth = leftSide.getBoundingClientRect().width;
    rightWidth = rightSide.getBoundingClientRect().width;

    // Attach the listeners to `document`
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  const mouseMoveHandler = function (e) {
    // How far the mouse has been moved
    const dx = e.clientX - x;

    const newLeftWidth = ((leftWidth + dx) * 100) / resizer.parentNode.getBoundingClientRect().width;
    const newRightWidth = ((rightWidth - dx) * 100) / resizer.parentNode.getBoundingClientRect().width;
    const rzRightWidth = newLeftWidth + newRightWidth > 99.75 ? newRightWidth - 0.8 : Math.floor(newRightWidth);
    if (newRightWidth > 23 && newLeftWidth > 23) {
      leftSide.style.width = `${newLeftWidth}%`;
      rightSide.style.width = `${rzRightWidth}%`;
    }

    resizer.style.cursor = 'col-resize';
    document.body.style.cursor = 'col-resize';

    leftSide.style.userSelect = 'none';
    leftSide.style.pointerEvents = 'none';

    rightSide.style.userSelect = 'none';
    rightSide.style.pointerEvents = 'none';
  };

  const mouseUpHandler = function () {
    resizer.style.removeProperty('cursor');
    document.body.style.removeProperty('cursor');

    leftSide.style.removeProperty('user-select');
    leftSide.style.removeProperty('pointer-events');

    rightSide.style.removeProperty('user-select');
    rightSide.style.removeProperty('pointer-events');

    // Remove the handlers of `mousemove` and `mouseup`
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
  };
  // Attach the handler
  if (reset) {
    resizer.removeEventListener('mousedown', mouseDownHandler);
  } else {
    resizer.addEventListener('mousedown', mouseDownHandler);
  }
};

const getWidthBoxRight = (left, center) => {
  const leftWidth = document?.getElementById(left)?.parentNode?.getBoundingClientRect()?.width;
  const rightWidth =
    document?.getElementById(center)?.getBoundingClientRect()?.width +
    document?.getElementById(left)?.getBoundingClientRect()?.width;
  let newWidth = leftWidth - rightWidth;
  if (!Number.isInteger(newWidth)) newWidth = Math.floor(newWidth);
  return newWidth < 100 ? '100%' : newWidth;
};

const checkTextByType = (value, level = 1, split) => {
  let [d1, d2, d3, d4] = value.split('');
  const space = d2 && d2 === split ? d2 : (d3 ?? 0);
  switch (level) {
    case 1:
      if (+d1 === 0) {
        if (+d2 === 0) {
          return false;
        }
      }
      if (
        (d1 && !Number.isFinite(+d1)) ||
        (d2 && d2 !== space && !Number.isFinite(+d2)) ||
        (space && space !== split)
      ) {
        return false;
      }
      break;
    case 2:
      if (+d1 === 0) {
        if (+d2 === 0) {
          return false;
        }
      }
      if ((space === d2 && +d1 <= 0) || (space === d2 && +d1 > 9)) {
        return false;
      }
      if (
        (d1 && !Number.isFinite(+d1)) ||
        (d2 && d2 !== space && !Number.isFinite(+d2)) ||
        (space && space !== split) ||
        (d1 && d2 && +d1.concat(d2) > 12)
      ) {
        return false;
      }
      break;
    case 3:
      if (
        (d1 && !Number.isFinite(+d1)) ||
        (d2 && !Number.isFinite(+d2)) ||
        (d3 && !Number.isFinite(+d3)) ||
        (d4 && !Number.isFinite(+d4)) ||
        value.length > 4
      ) {
        return false;
      }
      break;
    default:
      return true;
  }
  return true;
};

const checkInFormat = (value, split = '.') => {
  let level = 1;
  while (value !== '') {
    let [d1, d2, d3] = value.split('');
    const space = d2 && d2 === split ? d2 : (d3 ?? 0);
    if (!checkTextByType(value, level, split)) {
      return { result: false, level };
    }
    value = level === 3 ? '' : value.slice(space === d2 ? 2 : 3, value.length);
    level++;
  }
  return { result: true, level: level - 1 };
};

const onChangeCommentEditor = (editor, setState, isReadonly = false) => {
  if (!isReadonly) {
    setState(editor.getData());
  }
};

const imageTypes = ['jpg', 'png', 'jpeg'];

const exel = ['xlsx', 'xls', 'csv'];

function getFormValues(form) {
  const formData = new FormData(form);
  let values = {};
  for (const [inputName, val] of formData.entries()) {
    if (inputName.endsWith('[]')) {
      values[inputName] = values[inputName] ? [...values[inputName], val] : [val];
    } else {
      values[inputName] = val;
    }
  }
  return values;
}

export const SweetServerError = () => {
  Swal.fire({
    title: 'Oops...',
    text: 'Server Error....',
    icon: 'error',
  });
};

export function useScrollDirection() {
  const prevScrollY = useRef(0);
  const [goingUp, setGoingUp] = useState(false);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    if (prevScrollY.current < currentScrollY && goingUp) {
      setGoingUp(false);
    }
    if (prevScrollY.current > currentScrollY && !goingUp) {
      setGoingUp(true);
    }
    prevScrollY.current = currentScrollY;
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [goingUp]);
  return goingUp ? 'up' : 'down';
}

export const truncateText = (text, maxLength) => {
  if (!text) return '';
  // If the text is shorter than or equal to the maximum length, return it as it is
  if (text.length <= maxLength) return text;
  // Otherwise, find the last space before the maximum length minus 3 using a regular expression
  let lastSpace = text.slice(0, maxLength - 3).lastIndexOf(' ');
  // If there is no space, return the text sliced from the beginning to the maximum length minus 3 and add three dots
  if (lastSpace === -1) {
    return `${text.slice(0, maxLength - 3)}...`;
  }
  // Otherwise, return the text sliced from the beginning to the last space and add three dots
  return `${text.slice(0, lastSpace)}...`;
};

export const isValidBic = (bic) => {
  //NO1510804651581
  return ibantools.isValidBIC(bic);
};

export const clearFormatBankNumber = (input = '') => input.replace(/\./g, '');

export const reactDatePickerLocales = {
  no: {
    localize: {
      month: (index) =>
        [
          'Januar',
          'Februar',
          'Mars',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Desember',
        ][index],
      day: (index) => ['Sø', 'Ma', 'Ti', 'On', 'To', 'Fr', 'Lø'][index],
    },
    formatLong: {
      date: () => 'dd.MM.yyyy',
    },
  },
  en: {
    localize: {
      month: (index) =>
        [
          'Januar',
          'February',
          'March',
          'April',
          'May',
          'Jun',
          'July',
          'August',
          'September',
          'October',
          'November',
          'Desember',
        ][index],
      day: (index) => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][index],
    },
    formatLong: {
      date: () => 'dd.MM.yyyy',
    },
  },
  vn: {
    localize: {
      month: (index) =>
        [
          'Tháng 1',
          'Tháng 2',
          'Tháng 3',
          'Tháng 4',
          'Tháng 5',
          'Tháng 6',
          'Tháng 7',
          'Tháng 8',
          'Tháng 9',
          'Tháng 10',
          'Tháng 11',
          'Tháng 12',
        ][index],
      day: (index) => ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][index],
    },
    formatLong: {
      date: () => 'dd.MM.yyyy',
    },
  },
};

export const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => convertKeysToCamelCase(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelCaseKey = toCamelCase(key);
      result[camelCaseKey] = convertKeysToCamelCase(obj[key]);
      return result;
    }, {});
  }
  return obj;
};

export const generateCountryOptions = () => {
  const countryOptions = getNames()
    .map((countryName) => {
      const label = currencyCodes.countries().find((data) => data.includes(countryName));
      return {
        value: getCode(countryName),
        label,
      };
    })
    .filter((option) => option.value && option.label); // Filter out any countries without a code

  // Sort the array with 'NO' at the first position
  countryOptions.sort((a, b) => {
    if (a.value === 'NO') return -1;
    if (b.value === 'NO') return 1;
    return a.label.localeCompare(b.label);
  });

  return countryOptions;
};

export const removeUnderscoreAndCapitalize = (str) => {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

export const formatNumberWithSpace = (num) => {
  if (num == null) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Company option label, e.g. "Company AS (123456789)"; skip OrganisationNumber when null/empty
export const getCompanyLabel = ({ CompanyName, OrganisationNumber } = {}) =>
  OrganisationNumber ? `${CompanyName} (${OrganisationNumber})` : CompanyName;

export {
  setCookie,
  getCookie,
  deleteCookie,
  setLocalStorage,
  getLocalStorage,
  deleteLocalStorage,
  loadNewBody,
  revertBody,
  checkCompanyExist,
  getCurrentCompany,
  renderHTML,
  formatDatabase,
  bankNumber,
  dateFormat,
  formatView,
  capitalize,
  capitalizeSpace,
  getJsonFromUrl,
  mapDataExelToTable,
  regexDate,
  regexNaUy,
  normalizeImportDateString,
  formatCommon,
  blobToBase64,
  eventResizer,
  getWidthBoxRight,
  checkInFormat,
  onChangeCommentEditor,
  imageTypes,
  exel,
  getFormValues,
  isHtml,
};
